import * as api from '@apis/admin-user';
import * as authApi from '@apis/auth';
import * as membershipApi from '@apis/memberships';
import { ReactComponent as ChevronDownIcon } from '@assets/images/app/chevron-down-nutral90.svg';
import { ReactComponent as ChevronUpIcon } from '@assets/images/app/chevron-up-nutral90.svg';
import { ReactComponent as AttachmentIcon } from '@assets/images/app/icon-attachment.svg';
import PartnerProfileHeader1 from '@assets/images/app/partner-profile-header-1.png';
import PartnerProfileHeader2 from '@assets/images/app/partner-profile-header-2.png';
import PartnerProfileHeader3 from '@assets/images/app/partner-profile-header-3.png';
import PartnerProfileHeader4 from '@assets/images/app/partner-profile-header-4.png';
import PartnerProfileHeader5 from '@assets/images/app/partner-profile-header-5.png';
import PartnerProfileHeader6 from '@assets/images/app/partner-profile-header-6.png';
import PartnerProfileHeader7 from '@assets/images/app/partner-profile-header-7.png';
import PartnerProfileHeader8 from '@assets/images/app/partner-profile-header-8.png';
import PartnerProfileHeader9 from '@assets/images/app/partner-profile-header-9.png';
import { ReactComponent as QualifiedIcon } from '@assets/images/app/qualified-check-circle.svg';
import TutorDefaultAvatar from '@assets/images/app/tutorintro.png';
import { Divider } from '@components/common/Divider';
import { UserCreateModal } from '@components/user/UserCreateModal';
import { UserDetailModal } from '@components/user/UserDetailModal';
import { UserEditModal } from '@components/user/UserEditModal';
import { UserMembershipEditModal } from '@components/user/UserMembershipEditModal';
import UserPointEditModal from '@components/user/UserPointEditModal';
import { Roles, ROLE_LABELS } from '@consts/role';
import { useAppSocket } from '@hooks/appSocket';
import { useAuthentication } from '@hooks/authentication';
import { AdminUser, ProfileApprovalState } from '@models/auth';
import { Membership } from '@models/membership';
import { useAdminUsersStore } from '@stores/adminUsersStore';
import { useContentLoaderStore } from '@stores/contentLoaderStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { degreeValue, getAddressText, getChildrenText } from '@utils/dataTransform';
import { localTimeToUtcISOString, utcTimeToLocalISOString } from '@utils/date';
import { cdnImageUrl } from '@utils/image';
import { WEB_URL } from '@variables';
import { Button, DatePicker, DatePickerProps, Input, Modal, Popover, Select, Table } from 'antd';
import { FilterSelect, filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import moment from 'moment';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { useEffect, useRef, useState } from 'react';
import LinesEllipsis from 'react-lines-ellipsis';
import { PhotoView } from 'react-photo-view';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const HEADER_IMAGES = [
  PartnerProfileHeader1,
  PartnerProfileHeader2,
  PartnerProfileHeader3,
  PartnerProfileHeader4,
  PartnerProfileHeader5,
  PartnerProfileHeader6,
  PartnerProfileHeader7,
  PartnerProfileHeader8,
  PartnerProfileHeader9,
];

export const UsersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pointEditUser, setPointEditUser] = useState<AdminUser>();
  const [selectedTutorId, setSelectedTutorId] = useState<string>();
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const [childBirthdayStartInput, setChildBirthdayStartInput] = useState<string>('');
  const [childBirthdayEndInput, setChildBirthdayEndInput] = useState<string>('');
  const [editUserId, setEditUserId] = useState<string>();
  const [detailUserId, setDetailUserId] = useState<string>();
  const [editMembershipUserId, setEditMembershipUserId] = useState<string>();
  const [showUserCreate, setShowUserCreate] = useState<boolean>(false);
  const [showExperiences, setShowExperiences] = useState<boolean>(false);
  const [isExcelDownloading, setIsExcelDownloading] = useState<boolean>(false);
  const [isEmailAgreedExcelDownloading, setIsEmailAgreedExcelDownloading] = useState<boolean>(false);
  const [isSmsAgreedExcelDownloading, setIsSmsAgreedExcelDownloading] = useState<boolean>(false);
  const headerImageRef = useRef<string>(HEADER_IMAGES[Math.floor(Math.random() * 9)]);
  const [isGreetingClamped, setIsGreetingClamped] = useState<boolean>(false);
  const [expandGreeting, setExpandGreeting] = useState<boolean>(false);
  const appSocket = useAppSocket();
  const store = useAdminUsersStore();
  const loader = useContentLoaderStore();
  useAuthentication();
  const dialog = useOkCancelDialog();
  const membershipFromUrl = searchParams.get('membership') || '';
  const fromMembership = searchParams.get('from') === 'membership';
  const userSearchQuery = useQuery({
    queryKey: [`admin-search-users`, JSON.stringify(store.searchQuery), membershipFromUrl, fromMembership],
    queryFn: () =>
      api.searchUsers({
        ...store.searchQuery,
        roles: [Roles.PARENT],
        ...(membershipFromUrl ? { membership: membershipFromUrl } : {}),
        ...(fromMembership
          ? {
              keyword: undefined,
              page: 0,
              startDate: undefined,
              endDate: undefined,
              childBirthdayStart: undefined,
              childBirthdayEnd: undefined,
            }
          : {}),
      }),
  });

  const membershipsQuery = useQuery({
    queryKey: ['users-memberships-dropdown'],
    queryFn: () => membershipApi.searchMemberships({ page: 0, size: 500 }),
  });
  const membershipList = ((membershipsQuery.data?.data?.contents || []) as Membership[]).filter((item) => item.alias);

  const userQuery = useQuery({
    queryKey: [`user-item-${selectedTutorId}`],
    queryFn: () => api.getUser(selectedTutorId!),
    enabled: !!selectedTutorId,
  });

  const loginAsMutation = useMutation({
    mutationFn: authApi.loginAsUser,
  });

  const restoreDeletedUserMutation = useMutation({
    mutationFn: api.restoreDeletedUser,
    onSuccess: () => {
      toast.dark('탈퇴 복구 되었습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
      });
      userSearchQuery.refetch();
    },
  });

  const partnerProfileMutation = useMutation({
    mutationFn: api.updatePartnerProfile,
    onSuccess: async () => {
      toast.dark('파트너 프로필을 수정했습니다.', {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        pauseOnFocusLoss: false,
        draggable: true,
        progress: undefined,
        bodyStyle: {
          color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
          textAlign: 'center',
          fontFamily: 'Pretendard',
          fontSize: '14px',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '20px',
        },
        theme: 'dark',
      });
    },
  });

  const userInfo = userQuery.data?.data;

  const partnerAcamemics = userInfo?.partnerProfile?.academics || [];

  const partnerCareers = userInfo?.partnerProfile?.careers || [];

  const partnerCertificates = userInfo?.partnerProfile?.certificates || [];

  useEffect(() => {
    const applyMembershipFromUrl = () => {
      if (fromMembership && membershipFromUrl) {
        setSearchKeyword(undefined);
        setChildBirthdayStartInput('');
        setChildBirthdayEndInput('');
        useAdminUsersStore.getState().updateSearchQuery({
          membership: membershipFromUrl,
          keyword: undefined,
          page: 0,
          startDate: undefined,
          endDate: undefined,
          childBirthdayStart: undefined,
          childBirthdayEnd: undefined,
        });
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('from');
            return next;
          },
          { replace: true },
        );
        return;
      }

      const currentMembership = useAdminUsersStore.getState().searchQuery.membership || '';
      if (membershipFromUrl && currentMembership !== membershipFromUrl) {
        useAdminUsersStore.getState().updateSearchQuery({
          membership: membershipFromUrl,
          page: 0,
        });
      }
    };

    if (useAdminUsersStore.persist.hasHydrated()) {
      applyMembershipFromUrl();
      return;
    }
    return useAdminUsersStore.persist.onFinishHydration(applyMembershipFromUrl);
  }, [membershipFromUrl, fromMembership, setSearchParams]);

  useEffect(() => {
    setSearchKeyword(store.searchQuery.keyword || undefined);
  }, [store.searchQuery.keyword]);

  useEffect(() => {
    setChildBirthdayStartInput(store.searchQuery.childBirthdayStart || '');
    setChildBirthdayEndInput(store.searchQuery.childBirthdayEnd || '');
  }, [store.searchQuery.childBirthdayStart, store.searchQuery.childBirthdayEnd]);

  const isValidBirthdayInput = (value: string) => !value || /^\d{8}$/.test(value);

  const applyChildBirthdayFilter = () => {
    if (!isValidBirthdayInput(childBirthdayStartInput)) {
      toast.error('시작 날짜는 YYYYMMDD 형식으로 입력해주세요.');
      return;
    }
    if (!isValidBirthdayInput(childBirthdayEndInput)) {
      toast.error('종료 날짜는 YYYYMMDD 형식으로 입력해주세요.');
      return;
    }
    if (childBirthdayStartInput && childBirthdayEndInput && childBirthdayStartInput > childBirthdayEndInput) {
      toast.error('시작 날짜는 종료 날짜보다 이후일 수 없습니다.');
      return;
    }

    store.updateSearchQuery({
      childBirthdayStart: childBirthdayStartInput || undefined,
      childBirthdayEnd: childBirthdayEndInput || undefined,
      page: 0,
    });
  };

  useEffect(() => {
    const searchResult = userSearchQuery.data?.data;
    if (searchResult) {
      store.setAdminUsersContainer(searchResult);
    }
  }, [userSearchQuery.data?.data]);

  useEffect(() => {
    appSocket.socket.addSocketEventListener('upload_excel_status', 'upload_excel_status', (data) => {
      const status = (data.status || '').toLowerCase();
      if (status === 'done') {
        userSearchQuery.refetch();
      }
      if (status === 'done' || status === 'error') {
        loader.off();
      }

      if (data.status) {
        toast.error(data.message, {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          pauseOnFocusLoss: false,
          draggable: true,
          progress: undefined,
          theme: 'light',
        });
      }
    });
  }, []);

  window.onbeforeunload = () => {
    appSocket.socket.removeSocketEventListener('upload_excel_status', 'upload_excel_status');
  };

  const getExcelFile = async () => {
    setIsExcelDownloading(true);
    try {
      const fileName = `igogo-users-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
      const blob = await api.getSearchUserssExcelFile({ ...store.searchQuery, type: 'parent' });
      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        // IE variant
        (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
      } else {
        const url = window.URL.createObjectURL(blob.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode && link.parentNode.removeChild(link);
      }
    } finally {
      setIsExcelDownloading(false);
    }
  };

  const getEmailAgreedUsersExcelFile = async () => {
    setIsEmailAgreedExcelDownloading(true);
    try {
      const fileName = `igogo-users-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
      const blob = await api.getSearchUserssExcelFile({ ...store.searchQuery, emailAgreeFilter: true, type: 'parent' });
      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        // IE variant
        (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
      } else {
        const url = window.URL.createObjectURL(blob.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode && link.parentNode.removeChild(link);
      }
    } finally {
      setIsEmailAgreedExcelDownloading(false);
    }
  };

  const getSMSAgreedUsersExcelFile = async () => {
    setIsSmsAgreedExcelDownloading(true);
    try {
      const fileName = `igogo-users-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
      const blob = await api.getSearchUserssExcelFile({ ...store.searchQuery, smsAgreeFilter: true, type: 'parent' });
      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        // IE variant
        (window.navigator as any).msSaveOrOpenBlob(new Blob(blob.data), fileName);
      } else {
        const url = window.URL.createObjectURL(blob.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode && link.parentNode.removeChild(link);
      }
    } finally {
      setIsSmsAgreedExcelDownloading(false);
    }
  };

  const onChangeStartDate: DatePickerProps['onChange'] = (date) => {
    store.setStartDate(localTimeToUtcISOString(date ? new Date(date.toISOString()) : new Date(), { timeToZero: true }));
  };

  const onChangeEndDate: DatePickerProps['onChange'] = (date) => {
    store.setEndDate(localTimeToUtcISOString(date ? new Date(date.toISOString()) : new Date(), { timeToZero: true }));
  };

  const TABLE_COLUMNS: ColumnsType<AdminUser> = [
    {
      title: '기능',
      align: 'center',
      width: '140px',
      dataIndex: '_id',
      render: (_id) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <Button
            onClick={() => {
              navigator.clipboard.writeText(_id);
              toast.dark('사용자 ID를 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                hideProgressBar: true,
                closeOnClick: true,
                pauseOnHover: true,
                pauseOnFocusLoss: false,
                draggable: true,
                progress: undefined,
                bodyStyle: {
                  color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
                  textAlign: 'center',
                  fontFamily: 'Pretendard',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  lineHeight: '20px',
                },
                theme: 'dark',
              });
            }}>
            ID 복사
          </Button>
          <Button
            onClick={() => {
              dialog.open({
                title: '웹으로 이동',
                content: `선택하신 사용자로 로그인 후 웹으로 이동합니다.\n(어드민 계정은 로그아웃 됩니다)`,
                type: 'ok_cancel',
                onConfirm: () => {
                  loginAsMutation.mutate(_id, {
                    onSuccess: () => {
                      window.open(WEB_URL);
                    },
                  });
                },
              });
            }}>
            웹 로그인
          </Button>
        </div>
      ),
    },
    {
      title: '수정',
      align: 'center',
      width: '80px',
      render: (record) => {
        const _id = record._id;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
            <Button
              onClick={() => {
                setEditUserId(_id);
              }}>
              수정
            </Button>
            {record.deletedAt && (
              <Button
                type="dashed"
                onClick={() => {
                  dialog.open({
                    title: '탈퇴 복구',
                    content: '탈퇴 유저를 복구하시겠습니까?',
                    type: 'ok_cancel',
                    onConfirm: () => {
                      restoreDeletedUserMutation.mutate(_id);
                    },
                  });
                }}>
                탈퇴 복구
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: '상세보기',
      align: 'center',
      width: '90px',
      dataIndex: '_id',
      render: (_id) => (
        <Button
          type="primary"
          onClick={() => {
            setDetailUserId(_id);
          }}>
          상세보기
        </Button>
      ),
    },
    {
      title: '역할',
      width: '140px',
      dataIndex: 'roles',
      align: 'center',
      render: (roles) => {
        const roleText = (roles || []).map((r: string) => {
          return ROLE_LABELS[r] || r;
        });
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              alignItems: 'center',
            }}>
            <div style={{ width: '110px' }}>{roleText.join(' / ')}</div>
          </div>
        );
      },
    },
    {
      title: '관심분야',
      width: '120px',
      align: 'center',
      render: (record) => {
        return record.profile?.categorySubList ? (
          <div style={{ whiteSpace: 'pre-wrap' }}>{`${(record.profile?.categorySubList || []).join(',')}`}</div>
        ) : (
          <div style={{ whiteSpace: 'pre-wrap' }}>{`-`}</div>
        );
      },
    },
    {
      title: '주소',
      dataIndex: 'profile',
      width: '120px',
      align: 'center',
      render: (profile) => {
        return (
          <Popover
            content={profile?.address ? `${profile.address?.address} ${profile.address?.address2}` : '주소 없음'}>
            <div>{getAddressText({ availableArea: { specificAddress: profile?.address } })}</div>
          </Popover>
        );
      },
    },
    {
      title: '수신동의',
      dataIndex: 'profile',
      width: '120px',
      align: 'center',
      render: (profile) => {
        return (
          <div>
            <div>{`이메일: ${profile?.marketingEmailAgree ? '⭕' : 'X'}`}</div>
            <div>{`SMS: ${profile?.marketingSMSAgree ? '⭕' : 'X'}`}</div>
            <div>{`기타: ${profile?.otherAlarmAgree ? '⭕' : 'X'}`}</div>
          </div>
        );
      },
    },
    {
      title: '포인트',
      align: 'center',
      width: '120px',
      dataIndex: 'profile',
      render: (profile, record) => {
        return (
          <div>
            <div>{profile?.creditPoints ? profile.creditPoints.toLocaleString() : '-'}</div>
            <div>
              <Button
                onClick={() => {
                  setPointEditUser(record);
                }}>
                포인트 수정
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      title: '리워드 코드',
      align: 'center',
      width: '120px',
      render: (record) => {
        return (
          <div>
            <div>{record?.rewardCode || '-'}</div>
            {record?.referral?.rewardCode && (
              <div>
                <div>가입 코드</div>
                <div>{record?.referral?.rewardCode}</div>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '회원가입 구분',
      dataIndex: 'services',
      width: '100px',
      render: (services, record) => {
        const loginInfo: { names: string[]; emails: string[] } = { names: [], emails: [] };
        if (services?.kakao) {
          loginInfo.names.push('카카오');
          loginInfo.emails.push(services.kakao.email || '');
        }

        if (services?.naver) {
          loginInfo.names.push('네이버');
          loginInfo.emails.push(services.naver.email || '');
        }

        if (services?.apple) {
          loginInfo.names.push('애플');
          loginInfo.emails.push(services.apple.email || '');
        }

        if (loginInfo.names.length === 0 && record.emails) {
          loginInfo.names.push('이메일');
          loginInfo.emails.push(record.emails?.[0]?.address || '');
        }
        return (
          <div>
            <div>{loginInfo.names.join(', ')}</div>
            <Popover content={loginInfo.emails.join('\n')}>
              <div
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-all',
                  width: '90px',
                }}>
                {loginInfo.emails.join('\n')}
              </div>
            </Popover>
          </div>
        );
      },
    },
    {
      title: '이름',
      dataIndex: 'profile',
      align: 'center',
      render: (profile, record) => {
        return (
          <div>
            <button
              className="membership-badge"
              style={{
                background: record.membership?.color || 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: record.membership?.color ? 'white' : '#242424',
              }}
              onClick={() => {
                setEditMembershipUserId(record._id);
              }}>
              {record.membership?.name || '멤버십 없음'}
            </button>
            <div>{profile?.name}</div>
            {profile?.children && <div style={{ whiteSpace: 'pre-wrap' }}>{getChildrenText(profile?.children)}</div>}
            {profile?.nickname && <div>{profile?.nickname}</div>}
          </div>
        );
      },
    },
    {
      title: '전화번호',
      dataIndex: 'profile',
      align: 'center',
      width: '136px',
      render: (profile) => {
        if (!profile?.phone) {
          return '-';
        }
        return (
          <Button
            onClick={() => {
              navigator.clipboard.writeText(profile.phone);
              toast.info('전화번호를 클립보드에 복사했습니다.', {
                position: 'top-center',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                pauseOnFocusLoss: false,
                draggable: true,
                progress: undefined,
                theme: 'light',
              });
            }}>
            {profile.phone}
          </Button>
        );
      },
    },
    {
      title: '최근 활동일',
      width: '120px',
      align: 'center',
      render: (record) => {
        const activeAt = record.profile?.activeAt;
        if (!activeAt) {
          return '-';
        }
        return <div>{moment(activeAt).format('YYYY.MM.DD HH:mm')}</div>;
      },
    },
    {
      title: '가입',
      width: '120px',
      align: 'center',
      render: (record) => {
        return (
          <div>
            <div>{moment(record.createdAt).format('YYYY.MM.DD')}</div>
            {record.deletedAt && <div style={{ color: 'red' }}>{moment(record.deletedAt).format('YYYY.MM.DD')}</div>}
          </div>
        );
      },
    },
    {
      title: '메모',
      dataIndex: 'deletedAt',
      render: (memo) => {
        if (!memo) {
          return <div style={{ width: '28px' }}>-</div>;
        }
        return memo;
      },
    },
  ];

  return (
    <div className="dashboard" style={{ position: 'relative' }}>
      <UserEditModal
        open={!!editUserId}
        userId={editUserId}
        needRefetch={() => {
          userSearchQuery.refetch();
        }}
        onClose={() => {
          setEditUserId(undefined);
        }}></UserEditModal>
      <UserMembershipEditModal
        open={!!editMembershipUserId}
        userId={editMembershipUserId}
        needRefetch={() => {
          userSearchQuery.refetch();
        }}
        onClose={() => {
          setEditMembershipUserId(undefined);
        }}></UserMembershipEditModal>
      <UserCreateModal
        open={showUserCreate}
        onClose={() => {
          setShowUserCreate(false);
        }}
        needRefetch={() => {
          userSearchQuery.refetch();
        }}></UserCreateModal>
      <UserPointEditModal
        open={!!pointEditUser}
        user={pointEditUser}
        onClose={() => {
          setPointEditUser(undefined);
        }}
        needRefetch={() => {
          userSearchQuery.refetch();
        }}></UserPointEditModal>
      <UserDetailModal
        open={!!detailUserId}
        userId={detailUserId}
        onClose={() => {
          setDetailUserId(undefined);
        }}
      />
      <div className="page-header">
        <div>유저 관리</div>
        <div style={{ display: 'flex' }}>
          <div style={{ marginLeft: '10px' }}>
            <DatePicker
              onChange={onChangeStartDate}
              value={dayjs(utcTimeToLocalISOString(store.searchQuery.startDate as any))}
            />
          </div>
          <div style={{ marginLeft: '8px' }}>
            <DatePicker
              onChange={onChangeEndDate}
              value={dayjs(utcTimeToLocalISOString(store.searchQuery.endDate as any))}
            />
          </div>
        </div>
      </div>
      <Modal
        open={!!selectedTutorId}
        onCancel={() => {
          setSelectedTutorId(undefined);
        }}
        footer={
          <Button
            onClick={() => {
              setSelectedTutorId(undefined);
            }}>
            닫기
          </Button>
        }>
        <div>
          <OverlayScrollbarsComponent
            defer
            options={{ scrollbars: { autoHide: 'scroll' } }}
            style={{
              backgroundColor: '#ffffff',
              marginTop: 0,
              position: 'relative',
              height: 'calc(100dvh - 240px)',
              overflow: 'auto',
            }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', height: '44px', gap: '8px', alignItems: 'center', fontWeight: 'bold' }}>
                <div>{`${userInfo?.profile?.name}${
                  userInfo?.profile?.nickname ? `(${userInfo?.profile?.nickname})` : ''
                }`}</div>
                <div>
                  {userInfo?.profile?.contract?.licensePhoto ? (
                    <PhotoView src={cdnImageUrl(userInfo?.profile?.contract?.licensePhoto as string)}>
                      <Button>신분증</Button>
                    </PhotoView>
                  ) : (
                    <div>신분증 없음</div>
                  )}
                </div>
                <div>
                  {userInfo?.profile?.contract?.bankAccountPhoto ? (
                    <PhotoView src={cdnImageUrl(userInfo?.profile?.contract?.bankAccountPhoto as string)}>
                      <Button>통장</Button>
                    </PhotoView>
                  ) : (
                    <div>통장 없음</div>
                  )}
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <img
                  style={{
                    width: '88px',
                    height: '88px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    position: 'absolute',
                    left: '20px',
                    bottom: '-20px',
                  }}
                  src={userInfo?.profile?.imageUrl ? cdnImageUrl(userInfo?.profile.imageUrl) : TutorDefaultAvatar}
                  alt=""
                />
                <img
                  src={
                    userInfo?.partnerProfile?.profileMainImageUrl
                      ? cdnImageUrl(userInfo?.partnerProfile?.profileMainImageUrl)
                      : headerImageRef.current
                  }
                  style={{ objectFit: 'cover', width: '100%' }}
                  alt="partner-profile-header"
                />
              </div>
              <div style={{ marginTop: '44px', padding: '0 20px' }}>
                <div
                  style={{
                    overflow: 'hidden',
                    color: 'var(--Neutral-100, #242424)',
                    textOverflow: 'ellipsis',
                    fontFamily: 'Pretendard',
                    fontSize: '28px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                  }}>
                  {userInfo?.profile?.name}입니다.
                </div>
                {userInfo?.partnerProfile?.intro && (
                  <div style={{ marginTop: '8px' }}>
                    <div
                      style={{
                        color: 'var(--Neutral-80, #66686B)',
                        fontFamily: 'Pretendard',
                        fontSize: '14px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        whiteSpace: 'pre-wrap',
                      }}>
                      <LinesEllipsis
                        text={userInfo?.partnerProfile?.intro}
                        maxLine={expandGreeting ? 100 : 3}
                        onReflow={(value) => {
                          setIsGreetingClamped(value.clamped || expandGreeting);
                        }}
                        trimRight></LinesEllipsis>
                    </div>
                    {isGreetingClamped && (
                      <button
                        className="link-button"
                        style={{
                          color: 'var(--Blue-60, #1890FF)',
                          fontFamily: 'Pretendard',
                          fontSize: '14px',
                          fontStyle: 'normal',
                          fontWeight: 500,
                          textDecorationLine: 'underline',
                          textDecorationStyle: 'solid',
                          textDecorationSkipInk: 'none',
                          textDecorationThickness: 'auto',
                          textUnderlineOffset: 'auto',
                          textUnderlinePosition: 'from-font',
                          marginTop: '8px',
                        }}
                        onClick={() => {
                          setExpandGreeting((value) => !value);
                        }}>
                        {expandGreeting ? <div>접기</div> : <div>더보기</div>}
                      </button>
                    )}
                  </div>
                )}
              </div>
              {userInfo?.partnerProfile?.mainActivityZone && (
                <>
                  <Divider style={{ margin: '20px 0' }} />
                  <div
                    style={{
                      color: 'var(--Neutral-100, #242424)',
                      fontFamily: 'Pretendard',
                      fontSize: '20px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      padding: '0 20px',
                    }}>
                    <div>주요 활동 지역</div>
                  </div>
                  <div
                    style={{
                      overflow: 'hidden',
                      color: 'var(--Neutral-80, #66686B)',
                      textOverflow: 'ellipsis',
                      fontFamily: 'Pretendard',
                      fontSize: '14px',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      marginTop: '8px',
                      padding: '0 20px',
                    }}>
                    {userInfo?.partnerProfile?.mainActivityZone
                      .map((item) => {
                        return `${item.city} ${item.district}`;
                      })
                      .join(', ')}
                  </div>
                </>
              )}

              <Divider style={{ margin: '20px 0' }} />
              <button
                className="link-button"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: 'var(--Neutral-100, #242424)',
                  fontFamily: 'Pretendard',
                  fontSize: '20px',
                  fontStyle: 'normal',
                  fontWeight: 700,
                  padding: '0 20px',
                }}
                onClick={() => {
                  setShowExperiences((value) => !value);
                }}>
                활동 내역
                {showExperiences ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </button>
              {showExperiences && (
                <div style={{ padding: '20px' }}>
                  <div className={'partner-academy-content'} style={{ paddingBottom: '18px', paddingRight: '20px' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        marginBottom: '13px',
                        alignItems: 'center',
                      }}>
                      <div
                        style={{
                          overflow: 'hidden',
                          color: 'var(--Neutral-100, #242424)',
                          textOverflow: 'ellipsis',
                          fontFamily: 'Pretendard',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 700,
                        }}>
                        학력
                      </div>
                    </div>
                    {partnerAcamemics.map((item, index) => {
                      return (
                        <div key={`academic-${index}`}>
                          <div
                            style={{
                              flexDirection: 'row',
                              marginBottom: '10px',
                              alignItems: 'center',
                            }}>
                            <div
                              style={{
                                flex: 1,
                                paddingLeft: '10px',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginLeft: 0,
                              }}>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}>
                                <div
                                  style={{
                                    backgroundColor: '#dadcdf',
                                    width: '6px',
                                    height: '6px',
                                    borderStyle: 'solid',
                                    borderRadius: '6px',
                                    marginRight: '13px',
                                    marginLeft: 0,
                                    marginTop: 7,
                                  }}
                                />
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                  }}>
                                  {`${item.school} ${item.graduation} (${item.degree ? degreeValue(item.degree) : ''})`}
                                  {item.approvalState === 'confirmApproval' && <QualifiedIcon />}
                                  <Select
                                    style={{ width: '120px' }}
                                    value={item.approvalState}
                                    options={[
                                      { value: ProfileApprovalState.WAIT, label: '승인 대기' },
                                      { value: ProfileApprovalState.CONFIRM, label: '승인' },
                                      { value: ProfileApprovalState.NOTHING, label: '첨부파일 없음' },
                                      { value: ProfileApprovalState.DENIED, label: '반려' },
                                    ]}
                                    onChange={(value) => {
                                      item.approvalState = value;
                                      if (selectedTutorId) {
                                        const newValues = [...partnerAcamemics];
                                        newValues[index] = { ...item, approvalState: value };

                                        partnerProfileMutation.mutate({
                                          userId: selectedTutorId,
                                          body: { academics: newValues },
                                        });
                                      }
                                    }}
                                  />
                                </div>
                                {item.imageUrl && (
                                  <button
                                    className="link-button"
                                    onClick={() => {
                                      window.open(cdnImageUrl(item.imageUrl), '_blank');
                                    }}>
                                    <AttachmentIcon />
                                  </button>
                                )}
                              </div>
                              {item.year && item.graduation && item.degree && (
                                <div
                                  style={{
                                    fontSize: '12px',
                                    color: '#9b9da0',
                                    marginLeft: '20px',
                                    marginTop: '4px',
                                  }}>
                                  {item.year} - {item.graduation}{' '}
                                  {item.degree ? '(' + degreeValue(item.degree) + ' 과정)' : ''}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {partnerAcamemics.length === 0 && (
                      <div
                        style={{
                          color: '#9b9da0',
                          fontSize: '15px',
                          lineHeight: '22px',
                          marginBottom: '10px',
                        }}>
                        파트너님께서 학력 등록을 준비하고 계세요.
                      </div>
                    )}
                  </div>

                  <div className={'partner-career-content'} style={{ paddingBottom: '18px', paddingRight: '20px' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        marginBottom: '13px',
                        alignItems: 'center',
                      }}>
                      <div
                        style={{
                          overflow: 'hidden',
                          color: 'var(--Neutral-100, #242424)',
                          textOverflow: 'ellipsis',
                          fontFamily: 'Pretendard',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 700,
                        }}>
                        경력
                      </div>
                    </div>
                    {partnerCareers.map((item, index) => {
                      return (
                        (item.approvalState === 'confirmApproval' ||
                          item.approvalState === 'beforeApproval' ||
                          item.approvalState === 'waitApproval') && (
                          <div key={`career-${index}`}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                marginBottom: '10px',
                                alignItems: 'center',
                              }}>
                              <div style={{ flex: 1, paddingLeft: '10px', justifyContent: 'center' }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                  }}>
                                  <div
                                    style={{
                                      backgroundColor: '#dadcdf',
                                      width: '6px',
                                      height: '6px',
                                      borderStyle: 'solid',
                                      borderRadius: '6px',
                                      marginRight: '13px',
                                      marginLeft: 0,
                                      marginTop: '8px',
                                    }}
                                  />
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      alignItems: 'center',
                                      flexWrap: 'wrap',
                                    }}>
                                    {(item.career || '').split(' ').map((item, idx) => {
                                      return (
                                        <div
                                          key={`company-item-${idx}`}
                                          style={{
                                            fontSize: '15px',
                                            lineHeight: '22px',
                                            marginRight: '6px',
                                            color: '#424242',
                                          }}>
                                          {item}
                                        </div>
                                      );
                                    })}
                                    {item.approvalState === 'confirmApproval' && <QualifiedIcon />}
                                    <Select
                                      style={{ width: '120px' }}
                                      value={item.approvalState}
                                      options={[
                                        { value: ProfileApprovalState.WAIT, label: '승인 대기' },
                                        { value: ProfileApprovalState.CONFIRM, label: '승인' },
                                        { value: ProfileApprovalState.NOTHING, label: '첨부파일 없음' },
                                        { value: ProfileApprovalState.DENIED, label: '반려' },
                                      ]}
                                      onChange={(value) => {
                                        item.approvalState = value;

                                        if (selectedTutorId) {
                                          const newValues = [...partnerCareers];
                                          newValues[index] = { ...item, approvalState: value };

                                          partnerProfileMutation.mutate({
                                            userId: selectedTutorId,
                                            body: { careers: newValues },
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  {item.imageUrl && (
                                    <button
                                      className="link-button"
                                      onClick={() => {
                                        window.open(cdnImageUrl(item.imageUrl), '_blank');
                                      }}>
                                      <AttachmentIcon />
                                    </button>
                                  )}
                                </div>
                                {(item.startYearMonth || item.period) && (
                                  <div
                                    style={{
                                      fontSize: '12px',
                                      color: '#9b9da0',
                                      marginLeft: '20px',
                                      marginTop: '4px',
                                    }}>
                                    {item.startYearMonth
                                      ? `${item.startYearMonth.replace('-', '.')} - ${item.endYearMonth?.replace(
                                          '-',
                                          '.',
                                        )}`
                                      : item.period}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      );
                    })}
                    {partnerCareers.length === 0 && (
                      <div
                        style={{
                          color: '#9b9da0',
                          fontSize: '15px',
                          lineHeight: '22px',
                          marginBottom: '10px',
                        }}>
                        파트너님께서 경력 등록을 준비하고 계세요.
                      </div>
                    )}
                  </div>

                  <div
                    className={'partner-certificate-content'}
                    style={{ paddingBottom: '18px', paddingRight: '20px' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        marginBottom: '13px',
                      }}>
                      <div
                        style={{
                          overflow: 'hidden',
                          color: 'var(--Neutral-100, #242424)',
                          textOverflow: 'ellipsis',
                          fontFamily: 'Pretendard',
                          fontSize: '16px',
                          fontStyle: 'normal',
                          fontWeight: 700,
                        }}>
                        자격증
                      </div>
                    </div>
                    {partnerCertificates.map((item, index) => {
                      return (
                        (item.approvalState === 'confirmApproval' ||
                          item.approvalState === 'beforeApproval' ||
                          item.approvalState === 'waitApproval') && (
                          <div key={`certificate-${index}`}>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'row',
                                marginBottom: '10px',
                                alignItems: 'center',
                              }}>
                              <div style={{ flex: 1, paddingLeft: '10px', justifyContent: 'center' }}>
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'flex-start',
                                  }}>
                                  <div
                                    style={{
                                      backgroundColor: '#dadcdf',
                                      width: '6px',
                                      height: '6px',
                                      borderStyle: 'solid',
                                      borderRadius: '6px',
                                      marginRight: '13px',
                                      marginLeft: 0,
                                      marginTop: '8px',
                                    }}
                                  />
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'row',
                                      alignItems: 'center',
                                      flexWrap: 'wrap',
                                    }}>
                                    {(item.name || '').split(' ').map((item, idx) => {
                                      return (
                                        <div
                                          key={`name-item-${idx}`}
                                          style={{
                                            fontSize: '15px',
                                            color: '#424242',
                                            marginRight: '6px',
                                            lineHeight: '22px',
                                          }}>
                                          {item}
                                        </div>
                                      );
                                    })}
                                    {item.approvalState === 'confirmApproval' && <QualifiedIcon />}
                                    <Select
                                      style={{ width: '120px' }}
                                      value={item.approvalState}
                                      options={[
                                        { value: ProfileApprovalState.WAIT, label: '승인 대기' },
                                        { value: ProfileApprovalState.CONFIRM, label: '승인' },
                                        { value: ProfileApprovalState.NOTHING, label: '첨부파일 없음' },
                                        { value: ProfileApprovalState.DENIED, label: '반려' },
                                      ]}
                                      onChange={(value) => {
                                        item.approvalState = value;

                                        if (selectedTutorId) {
                                          const newValues = [...partnerCertificates];
                                          newValues[index] = { ...item, approvalState: value };

                                          partnerProfileMutation.mutate({
                                            userId: selectedTutorId,
                                            body: { certificates: newValues },
                                          });
                                        }
                                      }}
                                    />
                                  </div>
                                  {item.imageUrl && (
                                    <button
                                      className="link-button"
                                      onClick={() => {
                                        window.open(cdnImageUrl(item.imageUrl), '_blank');
                                      }}>
                                      <AttachmentIcon />
                                    </button>
                                  )}
                                </div>
                                {item.issuer && (
                                  <div
                                    style={{
                                      fontSize: '12px',
                                      color: '#9b9da0',
                                      marginLeft: '20px',
                                      marginTop: '4px',
                                    }}>
                                    {item.issuer} -{' '}
                                    {item.issuanceYearMonth ? `${item.issuanceYearMonth.replace('-', '.')}` : ''}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      );
                    })}
                    {partnerCertificates.length === 0 && (
                      <div
                        style={{
                          color: '#9b9da0',
                          fontSize: '15px',
                          lineHeight: '22px',
                          marginBottom: '10px',
                        }}>
                        파트너님께서 자격증 등록을 준비하고 계세요.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </OverlayScrollbarsComponent>
        </div>
      </Modal>
      <div style={{ display: 'flex' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div style={{ display: 'flex', gap: '12px', width: '100%', height: '52px', alignItems: 'center' }}>
            <FilterSelect
              inactiveValue="createdAt"
              value={store.searchQuery.sort || 'createdAt'}
              style={{ width: 180 }}
              onChange={(value) => {
                store.updateSearchQuery({ sort: value });
              }}
              options={[
                { value: 'createdAt', label: '최근 가입일 순' },
                { value: 'profile.activeAt', label: '최근 활동일 순' },
                { value: 'updatedAt', label: '최근 업데이트 순' },
              ]}
            />
            <FilterSelect
              inactiveValue=""
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="멤버십"
              style={{ width: 220 }}
              loading={membershipsQuery.isLoading}
              options={[
                { value: '', label: '전체 멤버십' },
                ...membershipList.map((item) => ({ value: item.alias, label: item.name })),
              ]}
              value={store.searchQuery.membership || ''}
              onChange={(value) => {
                const next = value ? String(value) : undefined;
                store.updateSearchQuery({ membership: next, page: 0 });
                setSearchParams(next ? { membership: next } : {}, { replace: true });
              }}
            />
            <Input
              className={filterActiveClassName(!!store.searchQuery.keyword)}
              style={{ width: '240px' }}
              value={searchKeyword}
              placeholder={'검색어 입력 후 엔터'}
              onChange={(e) => {
                setSearchKeyword(e.target.value);
              }}
              onKeyUp={(e) => {
                if (e.code === 'Enter' && searchKeyword) {
                  store.updateSearchQuery({ keyword: searchKeyword });
                }
              }}></Input>
            <Button
              onClick={() => {
                setSearchKeyword(undefined);
                setChildBirthdayStartInput('');
                setChildBirthdayEndInput('');
                store.resetSearchQuery();
                setSearchParams({}, { replace: true });
              }}>
              검색 초기화
            </Button>
            <div style={{ fontWeight: 'bold' }}>총 검색결과: {store.searchResultContainer.total}</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ whiteSpace: 'nowrap' }}>*자녀 생년월일</span>
              <Input
                style={{ width: '120px' }}
                placeholder="YYYYMMDD"
                value={childBirthdayStartInput}
                maxLength={8}
                onChange={(e) => {
                  setChildBirthdayStartInput(e.target.value.replace(/\D/g, ''));
                }}
              />
              <span>이상 ~</span>
              <Input
                style={{ width: '120px' }}
                placeholder="YYYYMMDD"
                value={childBirthdayEndInput}
                maxLength={8}
                onChange={(e) => {
                  setChildBirthdayEndInput(e.target.value.replace(/\D/g, ''));
                }}
              />
              <span>미만</span>
              <Button onClick={applyChildBirthdayFilter}>적용하기</Button>
            </div>
            <div style={{ display: 'flex', marginLeft: 'auto', gap: '12px', alignItems: 'center' }}>
              <Button
                onClick={() => {
                  setShowUserCreate(true);
                }}>
                신규 유저 생성
              </Button>
              <Button
                loading={isExcelDownloading}
                disabled={isExcelDownloading}
                style={{ marginLeft: '10px' }}
                onClick={() => {
                  getExcelFile();
                }}>
                검색결과 엑셀 파일 다운로드
              </Button>
              <Popover content={<div style={{ fontSize: '12px' }}>탈퇴, 이메일 주소 없는 유저 제외</div>}>
                <Button
                  loading={isEmailAgreedExcelDownloading}
                  disabled={isEmailAgreedExcelDownloading}
                  style={{ marginLeft: '10px' }}
                  onClick={() => {
                    getEmailAgreedUsersExcelFile();
                  }}>
                  이메일 수신동의 유저목록
                </Button>
              </Popover>
              <Popover content={<div style={{ fontSize: '12px' }}>탈퇴 유저 제외</div>}>
                <Button
                  loading={isSmsAgreedExcelDownloading}
                  disabled={isSmsAgreedExcelDownloading}
                  style={{ marginLeft: '10px' }}
                  onClick={() => {
                    getSMSAgreedUsersExcelFile();
                  }}>
                  SMS 수신동의 유저목록
                </Button>
              </Popover>
            </div>
          </div>
          <Table
            columns={TABLE_COLUMNS}
            scroll={{ x: 'max-content', y: 'calc(100dvh - 280px)' }}
            style={{ whiteSpace: 'pre-wrap' }}
            rowKey={(record) => record?._id}
            dataSource={store.searchResultContainer.contents.filter((user) => user?._id)}
            pagination={{
              current: (store.searchResultContainer.page || 0) + 1,
              pageSize: store.searchResultContainer.size || 20,
              total: store.searchResultContainer.total,
            }}
            loading={userSearchQuery.isLoading}
            onChange={(e) => {
              store.updateSearchQuery({ page: (e.current || 2) - 1, size: e.pageSize || 20 });
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default UsersPage;
